// Frontend socket.js
import { io } from "socket.io-client";

const SOCKET_URL = process.env.VUE_APP_SOCKET_URL || window.location.origin;

function getAuth() {
  return { token: localStorage.getItem("token") || "" };
}

const raw = io(SOCKET_URL, {
  auth: getAuth(),
  transports: ["polling"], // TEMP: force polling-only
  reconnection: true,
  reconnectionDelayMax: 5000,
});

raw.on("connect", () => {
  console.log("Socket connected (client):", raw.id, "url:", SOCKET_URL);
});

raw.on("disconnect", (reason) => {
  console.log("Socket disconnected (client):", raw.id, "reason:", reason);
});

raw.on("connect_error", (err) => {
  console.error("socket connect_error (client):", err);
});

raw.on("reconnect_attempt", () => {
  raw.auth = getAuth();
});

// expose underlying socket for console debugging
window.__socket = raw;

// Convenience wrapper API expected by components
function normalizeReadPayload(payload = {}) {
  const chatId = payload.chatId || payload.roomID;
  const userId = payload.userId || payload.fromUser;
  return {
    ...payload,
    chatId,
    roomID: chatId,
    userId,
    fromUser: userId,
    lastReadSequence: payload.lastReadSequence ?? null,
    messageIds: Array.isArray(payload.messageIds) ? payload.messageIds : [],
    readAt: payload.readAt || new Date().toISOString(),
  };
}

const socket = {
  // low-level passthroughs
  on: (...args) => raw.on(...args),
  off: (...args) => raw.off(...args),
  emit: (...args) => raw.emit(...args),
  raw,

  // high-level "on" helpers
  onTypingStart: (cb) => raw.on("typing-start", cb),
  onTypingStop: (cb) => raw.on("typing-stop", cb),
  onNewMessage: (cb) => raw.on("new-message", cb),
  onMessageDelivered: (cb) => raw.on("message-delivered", cb),
  onMessageRead: (cb) => raw.on("message-read", cb),
  onMessagesRead: (cb) => raw.on("messages-read", cb),
  onMessageDeleted: (cb) => raw.on("message-deleted", cb),
  onMessageDeletedForEveryone: (cb) => raw.on("message-deleted-for-everyone", cb),
  onReactionUpdated: (cb) => raw.on("reaction-updated", cb),
  onMessagePinned: (cb) => raw.on("message-pinned", cb),
  onRoomJoined: (cb) => raw.on("room-joined", cb),

  // high-level emit helpers (match server payloads used in this repo)
  sendTypingStart: (chatId, fromUser, fromUserName) =>
    raw.emit("typing-start", { chatId, roomID: chatId, fromUser, fromUserName }),

  sendTypingStop: (chatId, fromUser) =>
    raw.emit("typing-stop", { chatId, roomID: chatId, fromUser }),

  // NEW: sendNewMessage returns a Promise (acknowledgement)
  sendNewMessage: (payload, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      let called = false;
      const timer = setTimeout(() => {
        if (!called) {
          called = true;
          reject(new Error("socket sendNewMessage timeout"));
        }
      }, timeout);

      try {
        raw.emit("new-message", payload, (response) => {
          if (called) return;
          called = true;
          clearTimeout(timer);
          resolve(response);
        });
      } catch (err) {
        if (!called) {
          called = true;
          clearTimeout(timer);
          reject(err);
        }
      }
    });
  },

  // Generic helper for emits with ack/timeouts
  _emitWithAck: (event, payload, timeout = 8000) => {
    return new Promise((resolve, reject) => {
      let called = false;
      const timer = setTimeout(() => {
        if (!called) {
          called = true;
          reject(new Error(`socket ${event} timeout`));
        }
      }, timeout);

      try {
        raw.emit(event, payload, (resp) => {
          if (called) return;
          called = true;
          clearTimeout(timer);
          resolve(resp);
        });
      } catch (err) {
        if (!called) {
          called = true;
          clearTimeout(timer);
          reject(err);
        }
      }
    });
  },

  // Delivery / read / delete / reaction / pin emits (return Promises)
  sendMessageDelivered: (payload, timeout) => {
    const normalized = typeof payload === "object" ? payload : { chatId: payload };
    return socket._emitWithAck("message-delivered", normalized, timeout);
  },

  sendMessageRead: (payload, timeout) =>
    socket._emitWithAck("message-read", normalizeReadPayload(payload), timeout),

  sendMessagesRead: (payload, timeout) =>
    socket._emitWithAck("messages-read", normalizeReadPayload(payload), timeout),

  sendMessageDeleted: (payload, timeout) => socket._emitWithAck("message-deleted", payload, timeout),
  sendMessageDeletedForEveryone: (payload, timeout) =>
    socket._emitWithAck("message-deleted-for-everyone", payload, timeout),

  sendReactionUpdated: (payload, timeout) => socket._emitWithAck("reaction-updated", payload, timeout),

  // alias used by ChatMessages.vue
  sendReaction: (chatId, messageId, emoji, user, action, timeout) =>
    socket.sendReactionUpdated({ chatId, roomID: chatId, messageId, emoji, user, action }, timeout),

  sendMessagePinned: (payload, timeout) => socket._emitWithAck("message-pinned", payload, timeout),

  joinRoom: (room) => {
    if (!room) return;
    raw.emit("join-chat", { chatId: String(room) });
  },
  leaveRoom: (room) => {
    if (!room) return;
    raw.emit("leave-chat", { chatId: String(room) });
  },

  // aliases used by ChatMessages.vue
  joinChatRoom: (room) => {
    if (!room) return;
    raw.emit("join-chat", { chatId: String(room) });
  },
  leaveChatRoom: (room) => {
    if (!room) return;
    raw.emit("leave-chat", { chatId: String(room) });
  },

  // convenience to remove specific handlers by event name
  removeListener: (event, cb) => raw.off(event, cb)
};

export function joinRoom(room) {
  if (!room) return;
  socket.joinRoom(room);
}
export function leaveRoom(room) {
  if (!room) return;
  socket.leaveRoom(room);
}

export default socket;